import { http } from '@/providers/apis/http';
import { searchSerializer } from '@/providers/serializers/search/search';
import { courseSerializer } from '@/providers/serializers/content/course';
import { collectionSerializer } from '@/providers/serializers/content/collection';
import { assessmentSerializer } from '@/providers/serializers/content/assessment';
import { resourceSerializer } from '@/providers/serializers/content/resource';
import { questionSerializer } from '@/providers/serializers/content/question';
import { GOOGLE_API_KEY, SEARCH_API } from '@/utils/constants';
import { offlineActivitySerializer } from '@/providers/serializers/content/offlineActivity';

export class SearchAPI {

    private static INSTANCE = new SearchAPI();

    static get instance() {
        return this.INSTANCE;
    }

    private namespace = 'gooru-search/rest/v2/search';

    private namespace1 = 'gooru-search/rest/v1/pedagogy-search';

    private namespace2 = 'api/nucleus/v1';

    private namespace3 = 'gooru-search/rest/v1/cap';


    public fetchLearningMapPrerequisiteContents(competencyCode: string) {
        const namespace = this.namespace2;
        const endpoint = `${namespace}/taxonomy/competency/prerequisite/${competencyCode}`;
        const headers = http.getTokenHeaders();
        const options = {
            isDisplayCode: true,
        };
        return http.get(endpoint, headers).then((response) => {
            return response.data;
        });
    }

    public fetchLearningMapContents(competencyCode: string) {
        const namespace = this.namespace1;
        const endpoint = `${namespace}/learning-maps/standard/${competencyCode}`;
        const headers = http.getTokenHeaders();
        const options = {
            isDisplayCode: true,
        };
        return http.get(endpoint, headers).then((response) => {
            return searchSerializer.serializeLearningMapData(response.data);
        });
    }

    /**
     * @function fetchResources can used to fetch resources and questions
     * @param term
     * @param params
     */
    public fetchAggregationResources(params: any = {}) {
        const endpoint = `${this.namespace}/resource`;
        const headers = http.getTokenHeaders();
        const options = Object.assign({
            'q': '*',
            'isCrossWalk': false,
            'start': params.start,
            'length': params.length,
            'aggBy': 'contentSubFormat',
            'flt.publisherQualityIndicatorGTE': 2,
            'flt.audience': 'All Students,Teachers',
            'flt.contentFormat': 'resource',
        }, params);
        return http.get(endpoint, headers, options).then((response) => {
            return searchSerializer.serializeAggregation(response.data, 'resource');
        });
    }

    /**
     * @function fetchResources can used to fetch resources and questions
     * @param term
     * @param params
     */
    public fetchAggregationQuestion(params: any = {}) {
        const endpoint = `${this.namespace}/resource`;
        const headers = http.getTokenHeaders();
        const options = Object.assign({
            'q': '*',
            'isCrossWalk': false,
            'start': params.start,
            'length': params.length,
            'aggBy': 'contentSubFormat',
            'flt.audience': 'All Students,Teachers',
            'flt.resourceFormat': 'question',
        }, params);
        return http.get(endpoint, headers, options).then((response) => {
            return searchSerializer.serializeAggregation(response.data, 'question');
        });
    }

    /**
     * @function fetchCollections can used to fetch collection , assessment ,  offline-activity contents.
     * @param term
     * @param params
     */
    public fetchCollections(params: any = {}) {
        const endpoint = `${this.namespace}/scollection`;
        const headers = http.getTokenHeaders();
        const data = Object.assign({
            'q': '*',
            'isCrossWalk': false,
            'start': params.start || 1,
            'length': params.length || 10,
            'flt.collectionType': 'collection',
            'flt.publishStatus': 'published,unpublished',
        }, params);
        return http.get(endpoint, headers, data).then((response) => {
            // Note :- currently used only for total counts
            return collectionSerializer.normalizeSearchCollection(response.data);
        });
    }

    /**
     * @function fetchCollections can used to fetch collection , assessment ,  offline-activity contents.
     * @param term
     * @param params
     */
    public fetchOfflineActivity(params: any = {}) {
        const endpoint = `${this.namespace}/scollection`;
        const headers = http.getTokenHeaders();
        const data = Object.assign({
            'q': '*',
            'isCrossWalk': false,
            'start': params.start || 1,
            'length': params.length || 10,
            'flt.collectionType': 'offline-activity',
            'flt.publishStatus': 'published,unpublished',
        }, params);
        return http.get(endpoint, headers, data).then((response) => {
            return offlineActivitySerializer.normalizeSearchOfflineActivities(response.data);
        });
    }

    /**
     * @function fetchCollections can used to fetch collection , assessment ,  offline-activity contents.
     * @param term
     * @param params
     */
    public fetchAssessment(params: any = {}) {
        const endpoint = `${this.namespace}/scollection`;
        const headers = http.getTokenHeaders();
        const data = Object.assign({
            'q': '*',
            'isCrossWalk': false,
            'start': params.start || 0,
            'length': params.length || 10,
            'flt.collectionType': 'assessment',
            'flt.publishStatus': 'published,unpublished',
        }, params);
        return http.get(endpoint, headers, data).then((response) => {
            // Note :- currently used only for total counts
            return assessmentSerializer.normalizeSearchAssessments(response.data);
        });
    }

    /**
     * @function fetchCourse can used to fetch courses
     * @param term
     */
    public fetchCourse(params: any = {}) {
        const endpoint = `${this.namespace}/course`;
        const headers = http.getTokenHeaders();
        const data = Object.assign({
            q: '*',
            start: params.start || 0,
            length: params.length || 0,
            isCrossWalk: false,
        }, params);
        return http.get(endpoint, headers, data).then((response) => {
            // Note :- currently used only for total counts
            return courseSerializer.serializeCourses(response.data);
        });
    }

    /**
     * @function fetchResources can used to fetch resources
     * @param term
     */
    public fetchResources(params: any = {}) {
        const endpoint = `${this.namespace}/resource`;
        const headers = http.getTokenHeaders();
        const options = Object.assign({
            'q': '*',
            'isCrossWalk': false,
            'start': params.start || 0,
            'length': params.length || 10,
            // 'flt.publisherQualityIndicatorGTE': 2,
            // 'flt.audience': 'All Students,Teachers',
            'flt.contentFormat': 'resource',
        }, params);
        return http.get(endpoint, headers, options).then((response) => {
            return resourceSerializer.nomalizeSearchResources(response.data);
        });
    }

    /**
     * @function fetchQuestions can used to fetch questions
     * @param term
     */
    public fetchQuestions(params: any = {}) {
        const endpoint = `${this.namespace}/resource`;
        const headers = http.getTokenHeaders();
        const options = Object.assign({
            'q': '*',
            'isCrossWalk': false,
            'start': params.start || 0,
            'length': params.length || 10,
            'flt.audience': 'All Students,Teachers',
            'flt.resourceFormat': 'question',
        }, params);
        return http.get(endpoint, headers, options).then((response) => {
            return questionSerializer.normalizeSearchQuestions(response.data);
        });
    }

    /**
     * @function fetchRubrics can used to fetch rubrics
     * @param term
     */
    public fetchRubrics(term = '*', params: any = {}) {
        const endpoint = `${this.namespace}/rubric`;
        const headers = http.getTokenHeaders();
        const data = Object.assign({
            q: term,
            start: params.start || 0,
            length: params.length || 0,
            isCrossWalk: false,
        }, params);
        return http.get(endpoint, headers, data).then((response) => {
            // Note :- currently used only for total counts
            return response.data;
        });
    }

    /**
     * @function fetachLearnerContent can used to fetch library content
     * @param term
     */
     public fetachLearnerContents(params: any = {}) {
        const endpoint = `${this.namespace3}/content/search`;
        const headers = http.getTokenHeaders();
        const data = Object.assign({
            query: '*',
            filters: params,
        });

        return http.post(endpoint, headers, data).then((response) => {
            return searchSerializer.normalizeSearchLearningMapsContentInfo(response.data.contents);
        });
    }

    /**
     * @function googleSearch
     * Method to search google content
     */
    public googleSearch(query: string, start = 1) {
        const key = GOOGLE_API_KEY[Math.floor(Math.random() * GOOGLE_API_KEY.length)];
        const url = `${SEARCH_API.baseUrl}?key=${key}&cx=${SEARCH_API.googleCx}&q=${query}&start=${start}`;
        return http.get(url).then((response) => {
            return response.data;
        });
    }

    /**
     * @function bingSearch
     * Method to search bing content
     */
    public bingSearch(query: string, start = 1) {
        const key = GOOGLE_API_KEY[Math.floor(Math.random() * GOOGLE_API_KEY.length)];
        const url = `${SEARCH_API.baseUrl}?key=${key}&cx=${SEARCH_API.bingCx}&q=${query}&start=${start}`;
        return http.get(url).then((response) => {
            return response.data;
        });
    }

    /**
     * @function fetachLearnerContent can used to fetch library content
     * @param term
     * @param params
     */
    public fetachLearnerContent(term = '*', params: any = {}) {
        const endpoint = `${this.namespace1}/learning-maps`;
        const headers = http.getTokenHeaders();
        const data = {
            q: term,
            startAt: params.start || 0,
            length: params.length || 0,
            scopeKey: params.isMycontent ? 'my-content' : 'open-all',
        };

        return http.get(endpoint, headers, data).then((response) => {
            // Note :- currently used only for total counts
            return response.data;
        });
    }


    public fetchLearningMapCompetency(filters: any, start = 0, length = 0) {
        const namespace1 = this.namespace1;
        const requestURL = `${namespace1}/learning-maps/stats`;
        const headers = http.getTokenHeaders();
        const options = Object.assign({
            startAt: start,
            length,
            isCrosswalk: false,
        }, filters);

        return http.get(requestURL, headers, options).then((response) => {
            return searchSerializer.normalizeSearchlearningMapCompetency(response.data);
        });
    }


    public searchLearningMapCompetency(q = '*', start = 0, length = 0) {
        const namespace1 = this.namespace1;
        const requestURL = `${namespace1}/learning-maps/stats/search`;
        const headers = http.getTokenHeaders();
        const options = {
            startAt: start,
            length,
            isCrosswalk: false,
            q,
        };

        return http.get(requestURL, headers, options).then((response) => {
            return searchSerializer.normalizeSearchlearningMapCompetency(response.data);
        });
    }

    public learningMapsCompetencyContent(params: any, start = 0, length = 10) {
        const namespace1 = this.namespace1;
        const requestURL = `${namespace1}/learning-maps/competency/${params.id}`;
        const headers = http.getTokenHeaders();
        const options = {
            startAt: start,
            length,
            isCrosswalk: false,
        };
        return http.get(requestURL, headers, options).then((response) => {
            return searchSerializer.normalizeSearchlearningMapsContent(response.data);
        });
    }

    public searchCapLearnerContents(params: any = {}, pagination?: any) {
        const endpoint = `${this.namespace3}/content/search`;
        const headers = http.getTokenHeaders();
        const data = Object.assign({
            filters: params,
        });
        if (pagination && pagination.limit) {
            data.limit = pagination.limit;
        }
        if (pagination && pagination.pageNum) {
            data.pageNum = pagination.pageNum;
        }
        return http.post(endpoint, headers, data).then((response) => {
            return searchSerializer.normalizeSearchCapLearningMapsContentInfo(response.data.contents);
        });
    }

}

export const searchAPI = SearchAPI.instance;
